'use client';

import { useMounted } from '@/lib/hooks/useMounted';
import styles from './reviewSliderStylingOverride.module.css';
import { useLayoutContext } from '@/components/Layout/LayoutProvider';
import { useEffect, useRef } from 'react';

const RATING_OUT_OF = 5;

// ADA - Group star ratings and hide individual ones.
function labelStarGroup(group: HTMLElement) {
  if (group.dataset.a11yLabelled === 'true') return;

  const stars = group.querySelectorAll('.ti-star');
  if (!stars.length) return;

  let rating = 0;
  stars.forEach((star) => {
    if (star.classList.contains('f')) rating += 1;
    else if (star.classList.contains('h')) rating += 0.5;
    star.setAttribute('aria-hidden', 'true');
  });

  const value = Number.isInteger(rating) ? rating : rating.toFixed(1);
  group.setAttribute('role', 'img');
  group.setAttribute('aria-label', `${value} out of ${RATING_OUT_OF} stars`);
  group.dataset.a11yLabelled = 'true';
}

export default function ReviewSlider() {
  const isMounted = useMounted();
  const { trustIndexId01Long } = useLayoutContext();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMounted) return;
    const root = containerRef.current;
    if (!root) return;

    const handleReadMoreClick = (e: MouseEvent) => {
      const readMoreBtn = (e.target as HTMLElement).closest('.ti-read-more');
      if (!readMoreBtn) return;
      readMoreBtn
        .closest('.ti-review-item')
        ?.querySelector('.ti-review-content')
        ?.classList.toggle('scrollbar-visible');
    };
    root.addEventListener('click', handleReadMoreClick);
    let rafId = 0;
    const labelStars = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        root.querySelectorAll<HTMLElement>('.ti-stars').forEach(labelStarGroup);
      });
    };

    labelStars();

    const observer = new MutationObserver(labelStars);
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      root.removeEventListener('click', handleReadMoreClick);
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isMounted]);

  useEffect(() => {
    if (!isMounted) return;
    const root = containerRef.current;
    if (!root) return;

    const NAV_SELECTORS = ['.ti-controls', '.ti-controls-line'];

    const arrowEnabled = (el: Element | null) =>
      el != null && !(el.getAttribute('style') || '').includes('none');

    const geometryScrollable = () => {
      const viewport = root.querySelector<HTMLElement>('.ti-reviews-container');
      if (!viewport) return true; // widget not rendered yet → leave nav as-is
      return viewport.scrollWidth - viewport.clientWidth > 4;
    };

    const isNavigable = () => {
      const next = root.querySelector('.ti-controls .ti-next');
      const prev = root.querySelector('.ti-controls .ti-prev');
      if (next || prev) return arrowEnabled(next) || arrowEnabled(prev);
      return geometryScrollable();
    };

    const setHidden = (el: HTMLElement, hidden: boolean) => {
      const alreadyHidden = el.style.getPropertyValue('display') === 'none';
      if (hidden && !alreadyHidden) el.style.setProperty('display', 'none', 'important');
      else if (!hidden && alreadyHidden) el.style.removeProperty('display');
    };

    const update = () => {
      const hidden = !isNavigable();
      NAV_SELECTORS.forEach((selector) => {
        root.querySelectorAll<HTMLElement>(selector).forEach((el) => setHidden(el, hidden));
      });
    };

    let frame = 0;
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    scheduleUpdate();

    const mutationObserver = new MutationObserver(scheduleUpdate);
    mutationObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(root);

    return () => {
      cancelAnimationFrame(frame);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [isMounted]);

  if (!isMounted) {
    return <div className='w-full min-h-59.5 md:min-h-83'></div>;
  }

  return (
    <div
      ref={containerRef}
      className={styles.reviewSliderStylingOverride}
    >
      {trustIndexId01Long && (
        <div data-src={`https://cdn.trustindex.io/loader.js?${trustIndexId01Long}`}></div>
      )}
    </div>
  );
}
